import { AlertTriangle, Ban, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ClassifiedPostingLines, PostingLineFlag } from './attentionLines';

interface AttentionLinesPanelProps {
  lines: ClassifiedPostingLines;
  className?: string;
}

// Renders the blocking / excluded / attention line groups for a posting modal (86d3hm2vr).
// Nothing renders when all groups are empty. Left-border accent per severity, mirroring
// PricingDriftDialog's item pattern. Semantic tokens only.
export function AttentionLinesPanel({ lines, className }: AttentionLinesPanelProps) {
  const { blocking, excluded, attention } = lines;
  if (blocking.length === 0 && excluded.length === 0 && attention.length === 0) return null;

  return (
    <div className={cn('max-h-[40vh] space-y-3 overflow-y-auto', className)}>
      <Group
        title="Can't post — fix these first"
        icon={<Ban className="h-3.5 w-3.5 text-destructive" />}
        items={blocking}
        tone="destructive"
      />
      <Group
        title="Won't be posted"
        icon={<Info className="h-3.5 w-3.5 text-muted-foreground" />}
        items={excluded}
        tone="muted"
      />
      <Group
        title="Needs attention — you can still post"
        icon={<AlertTriangle className="h-3.5 w-3.5 text-warning" />}
        items={attention}
        tone="warning"
      />
    </div>
  );
}

function Group({
  title,
  icon,
  items,
  tone,
}: {
  title: string;
  icon: React.ReactNode;
  items: PostingLineFlag[];
  tone: 'destructive' | 'warning' | 'muted';
}) {
  if (items.length === 0) return null;
  const border =
    tone === 'destructive'
      ? 'border-l-destructive/50'
      : tone === 'warning'
        ? 'border-l-warning/50'
        : 'border-l-border';

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
        {icon}
        <span>
          {title} · {items.length}
        </span>
      </div>
      <ul className="space-y-1">
        {items.map((item) => (
          <li key={item.key} className={cn('border-l-2 pl-2.5', border)}>
            <p className="text-sm font-medium">{item.label}</p>
            <p className="text-xs text-muted-foreground">{item.reasons.join(' · ')}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}
